import { useContext, useState } from "react";
import styles from "./OnboardingVolunteer.module.scss";
import { useTranslation } from "../../i18n";
import { Button, CircularProgress, TextField } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import classNames from "classnames";
import { fetcher, isFetcherError } from "../../utils/fetcher";
import { AuthContext } from "../../context/AuthContext";
import { InputAddress } from "../InputAddress/InputAddress";
import { getMaxBirthDate, validateAge } from "../../utils/validation";

interface OnboardingVolunteerFirstStepProps {
  title: string;
  desc: string;
  onSetError: (error: string | false) => void;
  onDuplicateEmail: () => void;
}
interface FormData {
  first_name: string;
  last_name: string;
  phone_number: string;
  date_of_birth: string;
  permanent_address: string;
}

export const OnboardingVolunteerFirstStep = ({
  title,
  desc,
  onSetError,
  onDuplicateEmail,
}: OnboardingVolunteerFirstStepProps) => {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);

  const [formData, setFormData] = useState<FormData>({
    first_name: "",
    last_name: "",
    phone_number: "",
    date_of_birth: "",
    permanent_address: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const isValid =
    formData.first_name.trim() !== "" &&
    formData.last_name.trim() !== "" &&
    validateAge(formData.date_of_birth);

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      await fetcher({
        url: "/volunteer",
        method: "POST",
        body: JSON.stringify(formData),
      });
      authContext?.refreshUser();
    } catch (error) {
      if (isFetcherError(error) && error.status === 409) {
        onDuplicateEmail();
        return;
      }
      console.error("Error creating volunteer:", error);
      onSetError("onboarding.alerts.volFailed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={classNames(styles.content, styles.small)}>
      <h2>{title}</h2>
      <p>{desc}</p>
      <form className={styles.form}>
        <TextField
          label={t("volunteer.firstName")}
          value={formData.first_name}
          onChange={(e) =>
            setFormData({ ...formData, first_name: e.target.value })
          }
          required
          fullWidth
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label={t("volunteer.lastName")}
          value={formData.last_name}
          onChange={(e) =>
            setFormData({ ...formData, last_name: e.target.value })
          }
          required
          fullWidth
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          type="tel"
          label={t("volunteer.phone")}
          value={formData.phone_number}
          onChange={(e) =>
            setFormData({ ...formData, phone_number: e.target.value })
          }
          fullWidth
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
          placeholder="+420 123 456 789"
        />
        <TextField
          type="date"
          label={t("volunteer.dateOfBirth")}
          value={formData.date_of_birth}
          onChange={(e) =>
            setFormData({ ...formData, date_of_birth: e.target.value })
          }
          fullWidth
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
          helperText={t("onboarding.ageHelper")}
          inputProps={{
            max: getMaxBirthDate(),
          }}
        />
        <InputAddress
          editMode={true}
          label={t("volunteer.documentAddress")}
          address={{ address_text: formData.permanent_address }}
          onAddressChange={(value) =>
            setFormData((prevData) => ({
              ...prevData,
              permanent_address: value || "",
            }))
          }
        />
        <Button
          variant="contained"
          size="medium"
          endIcon={
            submitting ? <CircularProgress size={20} /> : <NavigateNextIcon />
          }
          onClick={handleCreate}
          disabled={!isValid || submitting}
        >
          {t("onboarding.continue")}
        </Button>
      </form>
    </div>
  );
};
